import React, { useState, useEffect, useCallback, useRef } from 'react';
import { saveAs } from 'file-saver';
import moment from 'moment';
import axios from 'axios';
import JSZip from 'jszip';
import * as Yup from 'yup';
import path from 'path';
import { connect } from 'react-redux';

import {
	Table,
	Menu,
	Dropdown,
	Button,
	Icon,
	Select,
	Modal,
	Typography,
	DatePicker,
	Divider,
	Tag,
	Breadcrumb,
	message,
	Checkbox,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import FileDuration from '../../../../components/FileDuration';
import FileExt from '../../../../components/FileExt';
import PageSizeHandler from '../../../../components/PageSizeHandle';
import PlayCell from '../../../../components/PlayCell';
import Form from '../../../../components/Form';
import { Container, OptionContent, TableHeader } from './styles';

import NewsAPI from '../../../../services/sdks/news';
import ProgramsAPI from '../../../../services/sdks/programs';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';

import { useElement, usePlayer, useDownload, useFilesValidator } from '../../../../hooks';
import {
	FiPlusCircle,
	FiShare2,
	FiTrash2,
	FiUploadCloud,
	FiXCircle,
	FiXOctagon,
} from 'react-icons/fi';
import FilesUploader from '../../../../components/FilesUploader';
import Progress from '../../../../components/Progress';
import { getResource } from '../../../../helpers/getResource';
import resourcesKeys from '../../../../constants/resourcesKeys';
import env from '../../../../config/env';

const News = ({ user }) => {
	const player = usePlayer();
	const download = useDownload();

	const {
		categorizedTypes: { news: categories },
		parseElementName,
	} = useElement();
	const { hasValidationError } = useFilesValidator();
	const uploaderRef = useRef();
	const [fallback, setFallback] = useState({ initialData: false });
	const [news, setNews] = useState([]);
	const [isGlobal, setIsGlobal] = useState(false);
	const [selectedNews, setSelectedNews] = useState([]);
	const [programs, setPrograms] = useState([]);
	const [visibleModals, setVisibleModals] = useState({ addNews: false });
	const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: null });
	const [progress, setProgress] = useState(null);
	const [newNews, setNewNews] = useState({
		category: undefined,
		programs: [],
		releaseDate: null,
		files: [],
	});

	const [filters, setFilters] = useState({
		program: undefined,
		category: undefined,
		releaseDate: null,
	});

	const columns = [
		{
			title: 'Categoria',
			key: 'category',
			render: (news) => (
				<PlayCell
					meta={{
						primary: parseElementName({ type: news?.category }),
						secondary: news?.program?.name,
					}}
					onPlay={() => {
						player.start({
							src: resolveFileSrc({ fileName: news?.filename, bucket: env.miscS3Bucket }),
							ref: news?._id,
							meta: {
								name: `NOTÍCIA - ${parseElementName({ type: news?.category })}`,
								artist: news?.program?.name,
							},
						});
					}}
					onPause={player.resume}
					isPlaying={player?.ref === news?._id && player?.isPlaying}
				/>
			),
		},
		{
			key: 'id',
			title: 'ID',
			align: 'center',
			render: ({ _id }) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					8
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Data de Lançamento',
			dataIndex: 'releaseDate',
			align: 'center',
			key: 'date',
		},
		{
			title: 'Duração',
			align: 'center',
			key: 'duration',
			render: (news) => (
				<FileDuration
					src={resolveFileSrc({ fileName: news?.filename, bucket: env.miscS3Bucket })}
				/>
			),
		},
		{
			title: 'Formato',
			align: 'center',
			key: 'ext',
			render: (news) => (
				<FileExt src={resolveFileSrc({ fileName: news?.filename, bucket: env.miscS3Bucket })} />
			),
		},
		getResource(user, resourcesKeys.NEWS_SHARING)
			? {
					title: 'Notícia Global?',
					align: 'center',
					key: 'isGlobal',
					render: (news) =>
						news?.isGlobal ? <Tag color='green'>Sim</Tag> : <Tag color='red'>Não</Tag>,
			  }
			: {},
		{
			title: 'Ações',
			align: 'right',
			key: 'options',
			render: (_, news, index) => (
				<>
					<Dropdown
						placement='bottomRight'
						overlay={
							<Menu>
								<Menu.Item
									key='play'
									onClick={() => {
										player.start({
											src: resolveFileSrc({ fileName: news?.filename, bucket: env.miscS3Bucket }),
											ref: news?._id,
											meta: {
												artist: 'Notícia',
												name: `NOTÍCIA REDE - ${parseElementName({ type: news?.category })} #${
													index + 1 + pagination?.pageSize * (pagination?.current - 1)
												} - ${news?.program?.name}`,
											},
										});
									}}>
									<Icon type='play-circle' /> Reproduzir
								</Menu.Item>
								<Menu.Item key='download' onClick={() => handleDownload({ news })}>
									<Icon type='download' /> Fazer download
								</Menu.Item>
								{getResource(user, resourcesKeys.NEWS_SHARING) && (
									<Menu.Item
										key='share'
										onClick={() => handleUpdateIsGlobalFlag([news._id], !news.isGlobal)}>
										{news.isGlobal ? (
											<>
												<FiXOctagon /> Remover compartilhamento
											</>
										) : (
											<>
												<FiShare2 /> Compartilhar{' '}
											</>
										)}
									</Menu.Item>
								)}

								<Menu.Divider />

								<Menu.Item
									key='delete'
									className='ant-dropdown-menu-item-danger'
									onClick={() => {
										Modal.confirm({
											title: 'Excluir notícia?',
											icon: 'exclamation-circle',
											content: 'Essa ação não poderá ser revertida, deseja continuar mesmo assim?',
											okText: 'Excluir',
											onOk: () => handleDelete({ newsId: news?._id }),
											okButtonProps: {
												icon: 'delete',
												type: 'danger',
											},
											cancelText: 'Cancelar',
											cancelButtonProps: {
												icon: 'close-circle',
											},
										});
									}}>
									<Icon type='delete' /> Excluir
								</Menu.Item>
							</Menu>
						}>
						<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 16 }} type='more' />
					</Dropdown>
				</>
			),
		},
	];

	const handleDownload = useCallback(
		async ({ news }) => {
			try {
				const name = parseElementName({ type: `NEWS-MAIN-${news?.category}` });
				return download({ filename: news?.filename, name });
			} catch (error) {
				console.error(error);
				message.error('Não foi possível fazer o download.');
			}
		},
		[download, parseElementName]
	);

	const handleDownloadAsZIP = useCallback(async () => {
		setFallback((prev) => ({ ...prev, loadingActions: true }));
		message.loading('Baixando...');

		const zip = new JSZip();

		for (let index = 0; index < selectedNews.length; index++) {
			const news = selectedNews[index];
			const filePath = resolveFileSrc({ fileName: news?.filename, bucket: env.miscS3Bucket });
			const blob = await axios.get(filePath, { responseType: 'blob' });
			const name = `NOTÍCIA REDE - ${parseElementName({ type: news?.category })}`;
			const ext = path.extname(news?.filename);
			const fName = `${news?.program?.name} - ${name}${ext}`;

			zip.file(fName, blob.data, { binary: true });
		}

		const zipContent = await zip.generateAsync({ type: 'blob' });
		const selectedProgram = programs.find(({ _id }) => _id === filters?.program);

		let downloadName = 'NOTÌCIAS REDE';

		selectedProgram && (downloadName = `${downloadName} - ${selectedProgram.name}`);
		filters?.releaseDate && (downloadName = `${downloadName} - ${filters.releaseDate}`);

		saveAs(zipContent, downloadName);
		message.destroy();
		setFallback((prev) => ({ ...prev, loadingActions: false }));
	}, [programs, selectedNews, parseElementName, filters]);

	const handleDelete = useCallback(async ({ newsId }) => {
		try {
			setFallback((prev) => ({ ...prev, deleting: true }));

			await NewsAPI.destroy({ newsId });

			setFallback((prev) => ({ ...prev, deleting: false }));
			setNews((news) => news.filter(({ _id }) => _id !== newsId));
		} catch (error) {
			console.error(error);
			message.error('Houve um erro ao deletar a notícia');

			setFallback((prev) => ({ ...prev, deleting: false }));
		}
	}, []);

	const handleMultiDelete = useCallback(async () => {
		setFallback((prev) => ({ ...prev, loadingActions: true }));
		message.loading('Deletando...');

		for (let index = 0; index < selectedNews.length; index++) {
			const { _id: newsId } = selectedNews[index];
			await handleDelete({ newsId });
		}

		setSelectedNews([]);
		setFallback((prev) => ({ ...prev, loadingActions: false }));
		message.destroy();
	}, [selectedNews, handleDelete]);

	const handleUpdateIsGlobalFlag = useCallback(async (newsIds, isGlobal) => {
		try {
			setFallback((prev) => ({ ...prev, loadingActions: true }));
			message.loading('Atualizando...');

			await NewsAPI.updateGlobalFlag({ payload: { newsIds, isGlobal } });

			message.destroy();
			message.success(
				isGlobal
					? 'As notícias foram disponibilizadas com sucesso'
					: 'O compartilhamento das notícias foi removido e as rádios não podem mais utilizar as notícias selecionadas',
				6
			);
			setFallback((prev) => ({ ...prev, loadingActions: false }));
			setNews((news) =>
				news.map((n) => {
					return newsIds.includes(n._id) ? { ...n, isGlobal } : n;
				})
			);
		} catch (error) {
			console.error(error);
			message.error('Houve um erro ao atualizar as notícias');

			setFallback((prev) => ({ ...prev, loadingActions: false }));
		}
	}, []);

	const createProgramNews = useCallback(
		async (programId, isLast) => {
			setProgress(0);

			const programName = programs.find((p) => p._id === programId)?.name ?? '';
			message.loading(`Enviando notícias do programa ${programName}`);

			const payload = new FormData();

			payload.append('category', newNews.category);
			payload.append('releaseDate', newNews.releaseDate);
			payload.append('program', programId);
			payload.append('user', user?._id);
			payload.append('isLocal', false);
			payload.append('isGlobal', isGlobal);
			newNews.files.forEach((file) => payload.append('file', file.data));

			await NewsAPI.store({
				payload,
				onUploadProgress: ({ total, loaded }) => {
					setProgress(Math.floor((loaded * 100) / total) - (isLast ? 0 : 1));
				},
			});
		},
		[isGlobal, newNews, programs, user]
	);

	const handleCreateNews = useCallback(async () => {
		try {
			if (hasValidationError(newNews.files)) {
				return message.warning(
					'Alguns dos arquivos selecionados estão corrompidos. Por favor, substitua-os por arquivos válidos.'
				);
			}

			const validationSchema = Yup.object().shape({
				category: Yup.string().required('Informe a categoria'),
				releaseDate: Yup.string().required('Informe a data de lançamento'),
				files: Yup.array().required('Selecione no mínimo 1 arquivo'),
			});

			await validationSchema.validate(newNews);
			setFallback((prev) => ({ ...prev, uploadingNews: true }));

			for (const [index, program] of Object.entries(newNews.programs)) {
				const isLast = Number(index) === newNews.programs.length - 1;
				await createProgramNews(program, isLast);
			}

			message.success('Notícias enviadas com sucesso', 3, () => {
				window.location.reload();
			});
		} catch (error) {
			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}
		}
	}, [hasValidationError, newNews, createProgramNews]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, current: 1 }));
	}, [filters]);

	useEffect(() => {
		const fetchNews = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingNews: true }));

				let query = `isLocal=false&page=${pagination?.current - 1}&limit=${
					pagination?.pageSize
				}&user=${user?._id}&`;

				filters?.program && (query = `${query}program=${filters.program}&`);
				filters?.releaseDate && (query = `${query}releaseDate=${filters.releaseDate}&`);
				filters?.category && (query = `${query}category=${filters.category}&`);

				const {
					data: { news, total },
				} = await NewsAPI.index({ query });

				setPagination((prev) => ({ ...prev, total }));

				setNews(news);
				setFallback((prev) => ({ ...prev, fetchingNews: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar as notícias');
			}
		};

		fetchNews();
	}, [filters, pagination.current, pagination.pageSize]); //eslint-disable-line

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const {
					data: { programs },
				} = await ProgramsAPI.index({
					isActive: true,
					isDeleted: false,
					pageNumber: 0,
					pageSize: 99999,
					userId: user?._id,
				});

				setPrograms(
					programs.sort((x, y) => {
						return x?.isFavorited === y?.isFavorited ? 0 : x?.isFavorited ? -1 : 1;
					})
				);
				setFallback((prev) => ({ ...prev, initialData: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os programas');
			}
		};

		if (user) {
			fetchInitialData();
		}
	}, [user]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Notícias' />

			<Container>
				<Breadcrumb
					style={{ marginBottom: 12 }}
					separator='>'
					routes={[{ breadcrumbName: 'INÍCIO' }, { breadcrumbName: 'NOTÍCIAS REDE' }]}
				/>

				<header>
					<Typography.Title level={2}>Notícias rede</Typography.Title>
					<Button
						type='primary'
						onClick={() => setVisibleModals({ ...visibleModals, addNews: true })}>
						<FiPlusCircle /> Adicionar notícias
					</Button>
				</header>

				<Form.Container layout='30% 30% 30%'>
					<Form.Item label='Filtrar por programa'>
						<Select
							showSearch
							value={filters.program}
							placeholder='Programa'
							onChange={(value) => setFilters({ ...filters, program: value })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							<Select.Option key='all' value={undefined} _search='TODOS'>
								TODOS
							</Select.Option>

							{programs.map((program) => (
								<Select.Option
									key={program._id}
									disabled={!program.isActive}
									_search={program?.name}>
									<OptionContent>
										<Icon
											theme='filled'
											type='heart'
											style={{
												color: 'var(--danger)',
												marginRight: 8,
												opacity: program?.isFavorited ? 1 : 0,
												pointerEvents: 'none',
											}}
										/>
										<span>
											{program?.name}
											{!program?.isActive && <Tag color='red'>Inadimplente</Tag>}
										</span>
									</OptionContent>
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Filtrar por categoria'>
						<Select
							showSearch
							value={filters.category}
							placeholder='Categoria'
							onChange={(category) => setFilters({ ...filters, category })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							<Select.Option key='all' value={undefined} _search='TODOS'>
								TODOS
							</Select.Option>

							{categories.map((category) => (
								<Select.Option value={category} key={category}>
									{parseElementName({ type: category }).replace('NOTÍCIA REDE - ', '')}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Filtrar por data'>
						<DatePicker
							placeholder='Data de lançamento'
							format='DD/MM/yyyy'
							value={filters?.releaseDate && moment(filters.releaseDate, 'DD/MM/yyyy')}
							onChange={(_, releaseDate) => setFilters({ ...filters, releaseDate })}
						/>
					</Form.Item>
				</Form.Container>

				<Divider />

				<TableHeader>
					<div className='actions'>
						<span>
							Quantidade: <strong>{pagination?.total}</strong>
						</span>
						<div>
							<Dropdown.Button
								disabled={!selectedNews.length || fallback.loadingActions}
								size='small'
								type='ghost'
								onClick={handleDownloadAsZIP}
								overlay={
									<Menu>
										<Menu.Item
											key='1'
											onClick={() => {
												handleUpdateIsGlobalFlag(
													selectedNews.map((n) => n._id),
													true
												);
											}}>
											<FiShare2 />
											Compartilhar notícias selecionadas
										</Menu.Item>

										<Menu.Item
											key='2'
											onClick={() => {
												handleUpdateIsGlobalFlag(
													selectedNews.map((n) => n._id),
													false
												);
											}}>
											<FiXOctagon />
											Remover compartilhamento
										</Menu.Item>

										<Menu.Divider />

										<Menu.Item
											key='3'
											onClick={() => {
												Modal.confirm({
													title: 'Deletar notícias selecionadas?',
													type: 'danger',
													content:
														'Todos as notícias selecionados serão excluídos e essa ação não poderá ser revertida, deseja continuar mesmo assim?',
													okText: 'Deletar',
													onOk: handleMultiDelete,
													okButtonProps: {
														icon: 'delete',
														type: 'danger',
													},
													cancelText: 'Cancelar',
													cancelButtonProps: {
														icon: 'close-circle',
													},
												});
											}}>
											<FiTrash2 />
											Deletar notícias selecionadas
										</Menu.Item>
									</Menu>
								}>
								Baixar
							</Dropdown.Button>

							{/* <Button
								size='small'
								disabled={!selectedNews.length}
								type='ghost'
								onClick={handleDownloadAsZIP}
								loading={fallback?.multiDownload}>
								<FiDownload /> Baixar selecionados{' '}
								{selectedNews.length !== 0 && `(${selectedNews.length})`}
							</Button>

							<Button
								size='small'
								disabled={!selectedNews.length}
								type='danger'
								onClick={() => {
									Modal.confirm({
										title: 'Deletar notícias selecionadas?',
										type: 'danger',
										content:
											'Todos as notícias selecionados serão excluídos e essa ação não poderá ser revertida, deseja continuar mesmo assim?',
										okText: 'Deletar',
										onOk: handleMultiDelete,
										okButtonProps: {
											icon: 'delete',
											type: 'danger',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<FiTrash2 /> Deletar selecionados{' '}
								{selectedNews.length !== 0 && `(${selectedNews.length})`}
							</Button> */}
						</div>
					</div>

					<PageSizeHandler pagination={pagination} setPagination={setPagination} />
				</TableHeader>

				<Table
					loading={fallback?.fetchingNews}
					size='middle'
					rowKey='_id'
					columns={columns}
					dataSource={news}
					style={{ border: 'none' }}
					pagination={{
						...pagination,
						size: 'large',
						onChange: (current) => setPagination({ ...pagination, current }),
					}}
					rowSelection={{
						onChange: (_, selectedRows) => setSelectedNews(selectedRows),
					}}
				/>
			</Container>

			<Modal
				width={600}
				visible={visibleModals?.addNews}
				onCancel={() => setVisibleModals({ ...visibleModals, addNews: false })}
				onOk={handleCreateNews}
				okButtonProps={{
					loading: fallback?.uploadingNews,
					disabled: !newNews?.files.length || !newNews?.category || !newNews?.programs.length,
				}}
				cancelButtonProps={{ disabled: fallback?.uploadingNews }}
				okText={
					<>
						<FiUploadCloud /> Enviar notícias
					</>
				}
				cancelText={
					<>
						<FiXCircle /> Cancelar
					</>
				}
				title={
					<>
						<FiPlusCircle /> Adicionar notícias
					</>
				}>
				<Form.Container>
					<Form.Item label='Categoria'>
						<Select
							showSearch
							value={newNews?.category}
							placeholder='Categoria'
							onChange={(category) => setNewNews({ ...newNews, category })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							{categories.map((category) => (
								<Select.Option
									key={category}
									value={category}
									_search={parseElementName({ type: category })}>
									{parseElementName({ type: category }).replace('NOTÍCIA REDE - ', '')}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Programas'>
						<Select
							showSearch
							mode='multiple'
							value={newNews.programs}
							placeholder='Programas'
							onChange={(programs) => setNewNews({ ...newNews, programs })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							{programs.map((program) => (
								<Select.Option
									key={program._id}
									disabled={!program.isActive}
									_search={program?.name}>
									<OptionContent>
										<Icon
											theme='filled'
											type='heart'
											style={{
												color: 'var(--danger)',
												marginRight: 8,
												opacity: program?.isFavorited ? 1 : 0,
												pointerEvents: 'none',
											}}
										/>
										<span>
											{program?.name}
											{!program?.isActive && <Tag color='red'>Inadimplente</Tag>}
										</span>
									</OptionContent>
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Data de lançamento'>
						<DatePicker
							placeholder='Data de lançamento'
							format='DD/MM/yyyy'
							value={newNews?.releaseDate && moment(newNews.releaseDate, 'DD/MM/yyyy')}
							onChange={(_, releaseDate) => setNewNews({ ...newNews, releaseDate })}
						/>
					</Form.Item>

					<FilesUploader
						multiple
						ref={uploaderRef}
						onChange={(files) => setNewNews((prev) => ({ ...prev, files }))}
						validTypes={['.mp3', '.wav']}
					/>

					{getResource(user, resourcesKeys.NEWS_SHARING) && (
						<Form.Item>
							<Checkbox checked={isGlobal} onChange={(e) => setIsGlobal(e.target.checked)}>
								Disponibilizar notícias para as demais rádios
							</Checkbox>
						</Form.Item>
					)}
				</Form.Container>
			</Modal>

			<Progress
				progress={progress}
				succesTitle='Notícias enviados com sucesso'
				title='Enviando notícias, por favor aguarde...'
			/>
		</>
	);
};

export default connect(({ user }) => ({ user }))(News);
